import type { MessageKey } from '@dijitalapp/i18n';
import { AI_ACTIONS, TEMPLATE_CATEGORIES, type AiAction, type AiRewriteResultDto, type AiStatusDto, type MessageTemplateDto } from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { Dialog } from './GroupDialogs';
import { PageEmpty, PageHeader } from './SectionNav';

/** AI assistant status, loaded once per mount. */
export function useAiStatus(): [AiStatusDto | null, (s: AiStatusDto) => void] {
  const [status, setStatus] = useState<AiStatusDto | null>(null);
  useEffect(() => {
    let alive = true;
    api<AiStatusDto>('GET', '/ai/status').then(
      (s) => alive && setStatus(s),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, []);
  return [status, setStatus];
}

// ---------- templates ----------

export function TemplatePicker({ draft, onInsert, onClose }: { draft: string; onInsert: (text: string) => void; onClose: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [templates, setTemplates] = useState<MessageTemplateDto[] | null>(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    api<{ templates: MessageTemplateDto[] }>('GET', '/templates').then(
      (r) => setTemplates(r.templates),
      (err: unknown) => setError(t(errorMessageKey(err))),
    );
  useEffect(() => {
    let alive = true;
    api<{ templates: MessageTemplateDto[] }>('GET', '/templates').then(
      (r) => alive && setTemplates(r.templates),
      (err: unknown) => alive && setError(t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [t]);

  const q = query.trim().toLocaleLowerCase(i18n.locale);
  const shown = (templates ?? []).filter(
    (x) =>
      (category === 'all' || (category === 'mine' ? x.own : x.category === category)) &&
      (!q || x.title.toLocaleLowerCase(i18n.locale).includes(q) || x.body.toLocaleLowerCase(i18n.locale).includes(q)),
  );

  const saveDraft = () => {
    const title = window.prompt(t('tpl.namePrompt'))?.trim();
    if (!title) return;
    setError(null);
    api('POST', '/templates', { title: title.slice(0, 80), body: draft.trim().slice(0, 2000), category: category in { general: 1, education: 1, work: 1, social: 1 } ? category : 'general' }).then(
      () => void load(),
      (err: unknown) => setError(t(errorMessageKey(err))),
    );
  };

  return (
    <Dialog title={t('tpl.title')} onClose={onClose}>
      <label className="visually-hidden" htmlFor="tpl-search">
        {t('tpl.search')}
      </label>
      <input id="tpl-search" className="input" type="search" placeholder={t('tpl.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
      <div className="row" role="tablist" aria-label={t('tpl.title')}>
        {['all', ...TEMPLATE_CATEGORIES, 'mine'].map((c) => (
          <button key={c} type="button" role="tab" aria-selected={category === c} className={`btn ${category === c ? 'btn-primary' : ''}`} onClick={() => setCategory(c)}>
            {c === 'all' ? '★' : c === 'mine' ? t('tpl.mine') : t(`tpl.category.${c}` as MessageKey)}
          </button>
        ))}
      </div>
      {!templates && !error && <p className="muted">{t('app.loading')}</p>}
      {templates && shown.length === 0 && <p className="muted">{t('tpl.empty')}</p>}
      <ul className="plain-list template-list">
        {shown.map((x) => (
          <li key={x.id}>
            <button
              type="button"
              className="template-item"
              onClick={() => {
                onInsert(x.body);
                onClose();
              }}
            >
              <strong>{x.title}</strong>
              <span className="hint">{x.body}</span>
            </button>
            {x.own && (
              <button
                type="button"
                className="link-button"
                onClick={() => void api('DELETE', `/templates/${x.id}`).then(load, (err: unknown) => setError(t(errorMessageKey(err))))}
              >
                {t('tpl.delete')}
              </button>
            )}
          </li>
        ))}
      </ul>
      <p className="hint">{t('tpl.hint')}</p>
      {error && <p className="alert alert-error">{error}</p>}
      <div className="row">
        <button className="btn" type="button" disabled={!draft.trim()} onClick={saveDraft}>
          {t('tpl.saveDraft')}
        </button>
        <span className="spacer" />
        <button className="btn" type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Dialog>
  );
}

// ---------- AI writing assistant ----------

function ConsentBox({ status, onChange }: { status: AiStatusDto; onChange: (s: AiStatusDto) => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [error, setError] = useState<string | null>(null);
  const set = (consent: boolean) =>
    api<AiStatusDto>('PUT', '/ai/consent', { consent }).then(onChange, (err: unknown) => setError(t(errorMessageKey(err))));
  return (
    <section className="stack" style={{ gap: 'var(--space-2)' }}>
      <h3>{t('ai.consentTitle')}</h3>
      <p>{t('ai.consentBody', { provider: status.provider === 'anthropic' ? 'Anthropic (Claude)' : (status.provider ?? 'AI') })}</p>
      {error && <p className="alert alert-error">{error}</p>}
      <div className="row">
        {status.consented ? (
          <button className="btn btn-danger" type="button" onClick={() => void set(false)}>
            {t('ai.consentWithdraw')}
          </button>
        ) : (
          <button className="btn btn-primary" type="button" onClick={() => void set(true)}>
            {t('ai.consentAccept')}
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * Suggests a rewrite of the user's own draft; nothing reaches the chat until the user applies it and
 * sends. In end-to-end encrypted chats a warning says the draft leaves the encryption.
 */
export function AiAssistDialog({ draft, encrypted, onApply, onClose }: { draft: string; encrypted: boolean; onApply: (text: string) => void; onClose: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const [status, setStatus] = useAiStatus();
  const [result, setResult] = useState<AiRewriteResultDto | null>(null);
  const [busy, setBusy] = useState<AiAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = (action: AiAction) => {
    setBusy(action);
    setError(null);
    setResult(null);
    api<AiRewriteResultDto>('POST', '/ai/rewrite', { action, text: draft }).then(
      (r) => {
        setResult(r);
        if (status) setStatus({ ...status, remainingToday: r.remainingToday });
      },
      (err: unknown) => setError(t(errorMessageKey(err))),
    ).finally(() => setBusy(null));
  };

  return (
    <Dialog title={t('ai.title')} onClose={onClose}>
      {!status && <p className="muted">{t('app.loading')}</p>}
      {status && !status.available && <p className="muted">{t('ai.unavailable')}</p>}
      {status?.available && !status.allowedForAccount && <p className="muted">{t('ai.notForAccount')}</p>}
      {status?.available && status.allowedForAccount && !status.consented && <ConsentBox status={status} onChange={setStatus} />}
      {status?.available && status.allowedForAccount && status.consented && (
        <>
          {encrypted && <p className="alert alert-error">🔒 {t('ai.e2eeWarning')}</p>}
          <div className="row">
            {AI_ACTIONS.filter((a) => a !== (i18n.locale === 'en' ? 'translate_en' : 'translate_tr')).map((a) => (
              <button key={a} className="btn" type="button" disabled={busy !== null || !draft.trim()} onClick={() => run(a)}>
                {busy === a ? t('ai.working') : t(`ai.action.${a}` as MessageKey)}
              </button>
            ))}
          </div>
          <p className="hint">{t('ai.remaining', { count: status.remainingToday })}</p>
          {result && (
            <section className="stack" style={{ gap: 'var(--space-2)' }}>
              <h3>{t('ai.suggestion')}</h3>
              <p className="ai-suggestion">{result.text}</p>
              {result.redacted > 0 && <p className="hint">🛡 {t('ai.redacted', { count: result.redacted })}</p>}
              {result.missingPlaceholders > 0 && <p className="hint e2ee-warning">⚠ {t('ai.missing', { count: result.missingPlaceholders })}</p>}
              <div className="row">
                <span className="spacer" />
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    onApply(result.text);
                    onClose();
                  }}
                >
                  {t('ai.use')}
                </button>
              </div>
            </section>
          )}
        </>
      )}
      {error && <p className="alert alert-error">{error}</p>}
      <div className="row">
        <span className="spacer" />
        <button className="btn" type="button" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Dialog>
  );
}

/** The "AI" page: what the assistant does, consent, today's allowance. */
export function AiPage() {
  const { i18n } = useApp();
  const { t } = i18n;
  const [status, setStatus] = useAiStatus();
  return (
    <div className="content-inner">
      <PageHeader title={t('ai.title')} description={t('ai.pageIntro')} />
      {!status && <p className="muted">{t('app.loading')}</p>}
      {status && !status.available && <PageEmpty icon="ai" title={t('ai.title')} body={t('ai.unavailable')} />}
      {status?.available && !status.allowedForAccount && <PageEmpty icon="ai" title={t('ai.title')} body={t('ai.notForAccount')} />}
      {status?.available && status.allowedForAccount && (
        <section className="card stack">
          <p>
            <strong>{status.consented ? `✓ ${t('ai.consentGiven')}` : t('ai.noConsent')}</strong> · {t('ai.remaining', { count: status.remainingToday })}
          </p>
          <ConsentBox status={status} onChange={setStatus} />
        </section>
      )}
    </div>
  );
}
